import { useEffect, useRef } from 'react';

export default function HeroCanvas() {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let w, h, raf, t = 0;
    let isVisible = true;
    const resize = () => { w = canvas.width = canvas.offsetWidth; h = canvas.height = canvas.offsetHeight; };
    resize();
    window.addEventListener('resize', resize);

    // Warm floating particles
    const particles = Array.from({ length: 60 }, () => ({
      x: Math.random(), y: Math.random(),
      vx: (Math.random()-0.5)*0.0002, vy:(Math.random()-0.5)*0.0002,
      r: Math.random()*2+0.5, alpha: Math.random()*0.25+0.05,
    }));

    const draw = () => {
      if (!isVisible) return; // Skip rendering when off-screen
      t += 0.006;
      ctx.clearRect(0, 0, w, h);

      // Warm cream gradient base
      const bg = ctx.createLinearGradient(0, 0, w, h);
      bg.addColorStop(0, 'rgba(245,241,235,0)');
      bg.addColorStop(0.5, 'rgba(237,232,223,0.3)');
      bg.addColorStop(1, 'rgba(245,241,235,0)');
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, w, h);

      // Elegant curtain folds — very soft gold lines
      for (let i = 0; i < 8; i++) {
        const baseX = (i / 7) * w;
        const phase = (i / 8) * Math.PI * 2;
        ctx.beginPath();
        ctx.moveTo(baseX, 0);
        for (let y = 0; y <= h; y += 4) {
          const wave = Math.sin(y * 0.006 + t + phase) * 22 + Math.sin(y * 0.012 + t * 0.8 + phase) * 11;
          ctx.lineTo(baseX + wave, y);
        }
        const alpha = 0.035 + Math.sin(t * 0.5 + phase) * 0.012;
        ctx.strokeStyle = `rgba(139,109,56,${alpha})`;
        ctx.lineWidth = 1;
        ctx.stroke();
      }

      // Soft radial glow at centre
      const cx = w/2, cy = h/2;
      const grd = ctx.createRadialGradient(cx, cy*0.7, 0, cx, cy*0.7, Math.min(w,h)*0.45);
      grd.addColorStop(0, 'rgba(201,168,76,0.06)');
      grd.addColorStop(1, 'transparent');
      ctx.fillStyle = grd;
      ctx.fillRect(0, 0, w, h);

      // Floating dust particles (warm gold)
      particles.forEach(p => {
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0) p.x = 1; if (p.x > 1) p.x = 0;
        if (p.y < 0) p.y = 1; if (p.y > 1) p.y = 0;
        ctx.beginPath();
        ctx.arc(p.x*w, p.y*h, p.r, 0, Math.PI*2);
        ctx.fillStyle = `rgba(139,109,56,${p.alpha})`;
        ctx.fill();
      });

      // Soft concentric rings
      for (let r = 80; r <= 320; r += 60) {
        ctx.beginPath();
        ctx.arc(cx, cy*0.7, r + Math.sin(t + r*0.01)*5, 0, Math.PI*2);
        ctx.strokeStyle = `rgba(201,168,76,${0.04 - r*0.00008})`;
        ctx.lineWidth = 0.8;
        ctx.stroke();
      }

      raf = requestAnimationFrame(draw);
    };
    draw();

    // Pause animation when canvas scrolls off-screen to save CPU/battery
    const visObs = new IntersectionObserver(([entry]) => {
      isVisible = entry.isIntersecting;
      if (isVisible && !raf) {
        raf = requestAnimationFrame(draw);
      }
    }, { threshold: 0.05 });
    visObs.observe(canvas);

    const ro = new ResizeObserver(resize);
    ro.observe(canvas);
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', resize); ro.disconnect(); visObs.disconnect(); };
  }, []);

  return (
    <canvas ref={ref} aria-hidden="true" style={{ position:'absolute', inset:0, width:'100%', height:'100%', pointerEvents:'none' }} />
  );
}
